import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { api, assetUrl } from '../lib/api';

const getEmbedUrl = (url) => {
  if (!url) return '';
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    setState('loading');
    api
      .get(`/posts/${slug}`)
      .then((res) => {
        setPost(res.data);
        setState('ready');
      })
      .catch(() => setState('error'));
  }, [slug]);

  if (state === 'loading') {
    return (
      <div className="py-32 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#26247B]" />
      </div>
    );
  }

  if (state === 'error' || !post) {
    return (
      <div className="py-24 text-center">
        <h1 className="text-2xl font-extrabold text-[#26247B]">Post not found</h1>
        <Link to="/news-insights" className="mt-4 inline-flex text-[#2E8B57] font-bold hover:underline">
          Back to News & Insights
        </Link>
      </div>
    );
  }

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return d;
    }
  };

  return (
    <article className="py-12 sm:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Link to="/news-insights" className="inline-flex items-center gap-2 text-[#2E8B57] font-bold text-sm mb-8 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to News & Insights
        </Link>
        <h1 data-testid="blog-post-title" className="text-[#26247B] font-extrabold text-3xl sm:text-4xl leading-tight tracking-tight">{post.title}</h1>
        <p className="mt-4 text-sm text-black/60 font-medium">
          by {post.author} | {formatDate(post.date)} | {post.category}
        </p>
        {post.image && (
          <img src={assetUrl(post.image)} alt={post.title} className="mt-8 w-full rounded-2xl shadow-[0_10px_30px_rgba(17,24,39,0.12)] object-cover max-h-[480px]" />
        )}
        {post.video_url && (
          <div className="mt-8 aspect-video rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(17,24,39,0.12)]">
            <iframe
              src={getEmbedUrl(post.video_url)}
              title={post.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
        <div className="post-body mt-10" dangerouslySetInnerHTML={{ __html: post.body }} />
      </div>
    </article>
  );
}
